import React, { useEffect, useState } from 'react'
import { Controller } from "react-hook-form"
import { formRulesBuilder } from "../lib/FormBuilderRules"
import { Box, Stack, Typography } from "@mui/material"
import c from "lib/style/colorVar.scss";
import ImagePicker from "../../ImagePicker/ImagePicker";
import { useRemoveMandatoryError } from "../Utils/FormBuilderUtils";

/**
 * @typedef {import("../lib/FormBuilderFieldBuilder").baseFormComponentProps} baseFormComponentProps
 * @typedef {import("../lib/FormBuilderFieldBuilder").baseConfig} baseConfig
 * @typedef {import("../lib/FormBuilderFieldBuilder").imagePickerConfig} imagePickerConfig
 * @param {baseFormComponentProps & baseConfig & imagePickerConfig} props
 */
const FormBuilderImagePicker = ({
  formProps,
  fieldName,
  mandatory = false,
  dependents = [],
  label,
  hideLabel,
  noErrorMessage = false,
  labelRemarks = "",
  importantRemarks = "",
  defaultImage,
  objectFit,
  allowedMimeTypes = [],
  maxSize,
  dimension = {},
  disabled = false,
  readOnly = false,
}) => {
  const showLabelPadding = hideLabel === "keepPadding";
  const removeLabel = hideLabel === "removePadding";

  const [loadedDefaultImage, setLoadedDefaultImage] = useState(null);

  useRemoveMandatoryError({ fieldName, mandatory, dependents, formProps });

  useEffect(() => {
    const currentImage = formProps.getValues(fieldName);

    if (defaultImage && !currentImage) {
      const readDefaultImage = async () => {
        const reader = new FileReader();
        const defaultImageBlob = await fetch(defaultImage).then((res) => res.blob());
        reader.onload = () => {
          setLoadedDefaultImage(reader.result);
          formProps.setValue(fieldName, reader.result);
        };
        reader.readAsDataURL(defaultImageBlob);
      }

      readDefaultImage();
    }
  }, [defaultImage]);

  return (
    <Stack direction="column">
      <Stack direction="row">
        {
          showLabelPadding &&
          // Simulate the height of the label
          <Box sx={{ height: "23px" }} />
        }
        {!removeLabel && <Typography variant="content">{label}
          {mandatory && <span style={{ color: c.danger }}> *</span>}
          {labelRemarks && <span style={{ color: c.blackDark666 }}> {labelRemarks}</span>}
        </Typography>}
      </Stack>
      {importantRemarks && <Typography variant="content" color={c.danger}>{importantRemarks}</Typography>}
      <Controller
        key={fieldName}
        name={fieldName}
        control={formProps.control}
        defaultValue={loadedDefaultImage}
        rules={formRulesBuilder({ mandatory })}
        render={({ field: { value, onChange, ref }, fieldState: { error } }) => (
          <ImagePicker
            formBuilderFocusRef={ref}
            noErrorMessage={noErrorMessage}
            currentImage={value}
            defaultImage={loadedDefaultImage}
            config={{
              objectFit,
              allowedMimeTypes,
              maxSize,
              dimension,
            }}
            onChange={onChange}
            onInit={(base64String) => onChange(base64String)}
            errorMessage={error?.message}
            onError={(errorMessage) => {
              formProps.setError(fieldName, { type: "manual", message: errorMessage })
            }}
            disabled={disabled}
            readOnly={readOnly}
          />
        )}
      />
    </Stack>
  )
}

export default FormBuilderImagePicker;